
import { useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AuthPageLayout from '@/components/auth/AuthPageLayout';

function ResetPasswordForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="flex-1 flex items-center justify-center p-4 sm:p-12 lg:p-24">
        <div className="w-full max-w-md space-y-8 text-center">
          <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto">
            <span className="material-icons text-red-500 text-3xl">error_outline</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Enlace inválido
            </h2>
            <p className="text-slate-500 dark:text-gray-400">
              Este enlace de recuperación no es válido o ha expirado.
            </p>
          </div>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-blue-600 transition-colors"
          >
            <span className="material-icons text-sm">refresh</span>
            Solicitar un nuevo enlace
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    // Alineado con backend (Keycloak min 8) y con Register (8-20). Mantener las
    // 3 reglas iguales evita UX divergente: el usuario aceptaba <8 acá y luego
    // el backend rechazaba.
    if (password.length < 8 || password.length > 20) {
      setError('La contraseña debe tener entre 8 y 20 caracteres');
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al restablecer la contraseña');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center p-4 sm:p-12 lg:p-24">
      <div className="w-full max-w-md space-y-8">
        <div className="flex md:hidden justify-center mb-6">
          <div className="flex items-center gap-2">
            <span className="material-icons text-primary text-3xl">account_balance_wallet</span>
            <span className="font-bold text-2xl tracking-tight text-gray-900 dark:text-white">FLUX</span>
          </div>
        </div>

        <div className="text-left">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center md:hidden">
              <span className="material-icons text-primary text-xl">password</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Nueva contraseña
            </h2>
          </div>
          <p className="text-slate-500 dark:text-gray-400 mt-2">
            Elige una contraseña segura para proteger tu cuenta.
          </p>
        </div>

        {success ? (
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-5 py-6 text-center">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                  <span className="material-icons text-green-600 dark:text-green-400 text-2xl">check_circle</span>
                </div>
              </div>
              <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-1">
                Contraseña restablecida
              </p>
              <p className="text-sm text-green-700 dark:text-green-400">
                Tu contraseña ha sido actualizada exitosamente.
              </p>
            </div>
            <Link
              to="/login"
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5"
            >
              Iniciar sesión
              <span className="absolute right-4 flex items-center">
                <span className="material-icons text-blue-200 group-hover:text-white transition-colors text-sm">
                  arrow_forward
                </span>
              </span>
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
                <span className="material-icons text-base">error</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Nueva contraseña
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-[20px]">lock</span>
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className="material-symbols-outlined text-gray-400 text-[20px] hover:text-gray-600">
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="confirm_password"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Confirmar contraseña
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400 text-[20px]">lock_reset</span>
                    </div>
                    <input
                      id="confirm_password"
                      name="confirm_password"
                      type={showConfirm ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-primary focus:border-primary text-sm dark:bg-surface-dark dark:text-white placeholder-gray-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className="material-symbols-outlined text-gray-400 text-[20px] hover:text-gray-600">
                        {showConfirm ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Guardando...' : 'Guardar contraseña'}
                <span className="absolute right-4 flex items-center">
                  <span className="material-icons text-blue-200 group-hover:text-white transition-colors text-sm">
                    arrow_forward
                  </span>
                </span>
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  return (
    <AuthPageLayout
      leftIcon="password"
      leftTitle={
        <>
          Seguridad en<br />cada paso
        </>
      }
      leftSubtitle="Protegemos tu cuenta y tus transacciones con los más altos estándares de seguridad bancaria."
    >
      <div className="absolute top-6 left-6 z-20">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400 hover:text-primary transition-colors font-medium"
        >
          <span className="material-icons text-sm">arrow_back</span>
          Volver al inicio de sesión
        </Link>
      </div>

      <Suspense fallback={<div className="flex-1 flex items-center justify-center" />}>
        <ResetPasswordForm />
      </Suspense>
    </AuthPageLayout>
  );
}
